<script lang="ts">
  /* The three things a current-aware route needs to be asked: whether to use
   * currents at all, how fast the boat goes, and when it leaves.
   *
   * Shared by the dash and the hosted chart. The router is one implementation
   * and the schedule is one component; these have to be one too, or the same
   * passage gets planned two subtly different ways depending which screen you
   * are standing at.
   *
   * ═══ WHY DEPARTURE ALSO PARKS THE TIME CURSOR ═══
   *
   * Setting a departure and leaving the chart showing NOW means planning a
   * passage against a tide you cannot see. Parking the cursor makes the
   * current field, the tidal-stream arrows and the tide stations behind this
   * panel show the water the plan is being made in — which is the whole reason
   * the cursor exists.
   */
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
  import { MAX_BOAT_KN, MIN_BOAT_KN, autoroute } from '@gennetay/chart-core/lib/autoroute.svelte';

  let { onchange }: { onchange?: () => void } = $props();

  /** `datetime-local` wants a local-time string with no zone, and gives one
   *  back. Both directions go through the same pair so a round trip cannot
   *  drift by a timezone — the class of bug that puts a passage an hour out
   *  and looks like a current model being wrong. */
  const toLocalInput = (t: number) => {
    const d = new Date(t);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const fromLocalInput = (v: string) => {
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  };

  const departValue = $derived(toLocalInput(autoroute.departT ?? Date.now()));
  const range = $derived(viewTime.range);

  function setDepart(v: string) {
    const t = fromLocalInput(v);
    if (t === null) return;
    /* Clamped to the shipped data, not to "any date the picker allows". Past
     * the end of the year there are no predictions and a schedule would be
     * fabricated. */
    const [lo, hi] = range ?? [t, t];
    const clamped = Math.min(Math.max(t, Math.min(lo, Date.now())), hi);
    autoroute.departT = clamped;
    viewTime.scrub(clamped);
    onchange?.();
  }

  function useNow() {
    autoroute.departT = null;
    viewTime.reset();
    onchange?.();
  }
</script>

<div class="pass">
  <label class="row on">
    <input
      type="checkbox"
      checked={autoroute.useCurrents}
      disabled={!autoroute.canUseCurrents()}
      onchange={(e) => {
        autoroute.setUseCurrents(e.currentTarget.checked);
        onchange?.();
      }}
    />
    <span class="txt">
      <span class="name">USE CURRENTS</span>
      <span class="sub">
        {#if autoroute.canUseCurrents()}
          Times the narrows to slack and carries the fair tide. Slower to solve.
        {:else}
          <!-- The row greys with a REASON rather than silently doing nothing —
               the same rule the layers panel follows. -->
          No current data on this host.
        {/if}
      </span>
    </span>
  </label>

  {#if autoroute.useCurrents}
    <div class="row">
      <span class="name">SPEED</span>
      <span class="fld">
        <input
          type="number"
          min={MIN_BOAT_KN}
          max={MAX_BOAT_KN}
          step="0.5"
          value={autoroute.boatKn}
          onchange={(e) => {
            autoroute.setBoatKn(Number(e.currentTarget.value));
            onchange?.();
          }}
        />
        <span class="unit">kn</span>
      </span>
    </div>

    <div class="row">
      <span class="name">DEPART</span>
      <span class="fld">
        <input
          type="datetime-local"
          value={departValue}
          onchange={(e) => setDepart(e.currentTarget.value)}
        />
        <button class="now" class:on={autoroute.departT === null} onclick={useNow}>NOW</button>
      </span>
    </div>
    <p class="sub hint">
      Speed through the water, not over the ground — the router works the rest out.
    </p>
  {/if}
</div>

<style>
  .pass {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    font: 11px/1.3 var(--mono);
  }
  .row.on {
    align-items: flex-start;
    cursor: pointer;
  }
  .txt {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .name {
    color: var(--ink);
    letter-spacing: 0.06em;
    flex: none;
    min-width: 5.5em;
  }
  .sub {
    font: 10px/1.35 var(--sans);
    color: var(--sub);
  }
  .hint {
    margin: 0;
  }
  .fld {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  input[type='number'] {
    width: 4.5em;
  }
  input[type='number'],
  input[type='datetime-local'] {
    background: var(--panel);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 4px;
    padding: 4px 6px;
    font: 11px/1 var(--mono);
  }
  .unit {
    color: var(--sub);
  }
  .now {
    background: none;
    border: 1px solid var(--line);
    border-radius: 4px;
    color: var(--sub);
    font: 10px/1 var(--mono);
    padding: 5px 7px;
    cursor: pointer;
  }
  .now.on {
    color: var(--cy);
    border-color: var(--cy);
  }

  /* iOS zooms the page when a focused control's text is under 16 px, and both
     of these take focus. The hosted app has a global rule for this; the dash
     does not, so it is carried with the component that needs it. */
  @media (pointer: coarse) {
    input[type='number'],
    input[type='datetime-local'] {
      font-size: 16px;
      min-height: 44px;
    }
    .now {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* ═══ THE CHECKBOX ROW IS THE TARGET, AND ON A PHONE IT HAS TO SAY SO ═══
   *
   * `USE CURRENTS` is the gate for everything below it — the whole
   * current-aware router hangs off this one box — and the box itself renders at
   * the browser default 13 px. The `<label>` around it has always been the real
   * target, which is why this is not a hit-area bug, but on a one-line row that
   * label measured 28 px and there was nothing to tell a thumb where to land.
   *
   * Narrowed to a PHONE, which is not a width: held sideways one is 734-852 px
   * wide and under 400 tall, so the query has to ask about both. The boat's
   * iPad is 1024 x 768 and matches neither arm, so it keeps the denser row its
   * own layout and its own proofs are built around. */
  @media (pointer: coarse) and (max-width: 640px),
    (pointer: coarse) and (max-height: 500px) {
    .row.on {
      min-height: 44px;
      align-items: center;
      gap: 12px;
    }
    .row.on input[type='checkbox'] {
      width: 22px;
      height: 22px;
      flex: none;
    }
  }
</style>
